import React, { useCallback, useEffect, useRef } from 'react';

import { Spin } from 'antd';
import { GetActionsStatsQuery } from 'api/server';
import { createCn } from 'bem-react-classname';
import Chart from 'chart.js';
import cn from 'classnames';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { AppThunk, server } from 'store';

import { getDashboardTimeChartConfig } from '../../utils/chartOptionsUtil';

import './ActionsStatsChart.scss';

const cx = createCn('ActionsStatsChart');

interface Props {
  className?: string;
  header?: string;
  fromDate: string;
}

export const ActionsStatsChart: React.FC<Props> = ({
  className,
  header,
  fromDate,
}) => {
  const dispatch = useDispatch();
  const chartRef = useRef<HTMLCanvasElement>(null);

  const getActionsStatsThunk = useCallback(
    (query: GetActionsStatsQuery): AppThunk => async (dispatch, getState) => {
      await dispatch(
        server.getActionsStats.thunk.request({
          query,
        }),
      );

      const { data } = server.getActionsStats.selector.state(getState());

      if (data && chartRef.current) {
        new Chart(
          chartRef.current,
          getDashboardTimeChartConfig({
            borderColor: '#ff9718',
            yLabelString: 'Actions',
            data: data.list.map((item) => ({
              x: item.date,
              y: item.amount,
            })),
          }),
        );
      }
    },
    [chartRef],
  );

  const { isFetching, isFetched } = useSelector(
    server.getActionsStats.selector.state,
  );

  useEffect(() => {
    dispatch(
      getActionsStatsThunk({
        timezone: moment.tz.guess(true),
        fromDate: moment(fromDate).startOf('day').format(),
      }),
    );
  }, [dispatch, fromDate, getActionsStatsThunk]);

  return (
    <div className={cn(className, cx())}>
      <div className={cx('container')}>
        {header && (
          <div className={cx('header')}>
            <h3>{header}</h3>
          </div>
        )}

        <div className={cx('graphArea')}>
          {isFetching && !isFetched && (
            <div className={cx('spin')}>
              <Spin size="large" />
            </div>
          )}
          <canvas ref={chartRef} />
        </div>
      </div>
    </div>
  );
};
